import {useCallback, useEffect, useState, useRef} from 'react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCookieTracker} from '~/app/utils/cookieTracking/CookieTrackerProvider'
import {useLocation} from 'react-router-dom'
import queryString from 'query-string'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useClientGlobalPreferences} from 'hec/context/global-preferences'

const useTpaMarketingConsent = () => {
    const {data: customer} = useCurrentCustomer()
    const globalCustomPreferences = useClientGlobalPreferences()
    const {cookieValues} = useCookieTracker()
    const location = useLocation()
    const updateCustomerMutation = useShopperCustomersMutation('updateCustomer')

    const [trustArcConsentState, setTrustArcConsentState] = useState(true)
    const consentChangeCallbacksRef = useRef([])

    const getCurrentAfid = useCallback(() => {
        const queryParams = queryString.parse(location.search)
        return queryParams?.AFID || queryParams?.afid || cookieValues?.tpaBranding?.afid || null
    }, [location.search, cookieValues])

    const isTpaUser = useCallback(() => {
        return getCurrentAfid() !== null
    }, [getCurrentAfid])

    const getIsMarketingAllowedForTpa = useCallback(() => {
        const afid = getCurrentAfid()
        if (!afid) return false

        try {
            const tpaConfigRaw = globalCustomPreferences?.TPA?.TPAConfig
            if (!tpaConfigRaw) return false

            const tpaConfig =
                typeof tpaConfigRaw === 'string' ? JSON.parse(tpaConfigRaw) : tpaConfigRaw
            const afidNumber = Number(afid)

            for (const tpaEntry of Object.values(tpaConfig)) {
                if (Number(tpaEntry.affiliateID) === afidNumber) {
                    return tpaEntry.marketing?.['AvailableBalance-IsMarketingAllowed'] === true
                }
            }
        } catch {
            return false
        }

        return false
    }, [getCurrentAfid, globalCustomPreferences])

    const hasTrustArcConsent = useCallback(() => {
        if (typeof window === 'undefined') return true // SSR

        // Check if TrustArc is loaded
        if (!window.truste || !window.truste.util) return true

        try {
            const consentCookie = window.truste.util.readCookie('notice_gdpr_prefs', true)
            if (!consentCookie) return true
            return consentCookie.includes('1') || consentCookie.includes('2')
        } catch {
            return true
        }
    }, [])

    const canTrackMarketingEvents = useCallback(() => {
        if (!hasTrustArcConsent()) return false
        if (!isTpaUser()) return true
        if (customer?.c_tpaLearningCenterConsentData === true) return true
        if (getIsMarketingAllowedForTpa()) return true
        return false
    }, [customer, hasTrustArcConsent, isTpaUser, getIsMarketingAllowedForTpa])

    const setExplicitConsent = useCallback(async () => {
        if (!customer?.customerId) return false

        try {
            await updateCustomerMutation.mutateAsync({
                parameters: {customerId: customer.customerId},
                body: {
                    c_tpaLearningCenterConsentData: true
                }
            })
            consentChangeCallbacksRef.current.forEach((callback) => callback())
            return true
        } catch {
            return false
        }
    }, [customer, updateCustomerMutation])

    const onConsentChange = useCallback((callback) => {
        consentChangeCallbacksRef.current.push(callback)

        // Return cleanup function
        return () => {
            consentChangeCallbacksRef.current = consentChangeCallbacksRef.current.filter(
                (cb) => cb !== callback
            )
        }
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return

        const handleTrustArcConsent = () => {
            setTrustArcConsentState(hasTrustArcConsent())
            consentChangeCallbacksRef.current.forEach((callback) => callback())
        }

        document.addEventListener('truste-consent', handleTrustArcConsent)
        document.addEventListener('truste-cookie', handleTrustArcConsent)
        handleTrustArcConsent()

        return () => {
            document.removeEventListener('truste-consent', handleTrustArcConsent)
            document.removeEventListener('truste-cookie', handleTrustArcConsent)
        }
    }, [hasTrustArcConsent])

    return {
        canTrackMarketingEvents,
        setExplicitConsent,
        isTpaUser: isTpaUser(),
        hasTrustArcConsent: trustArcConsentState,
        onConsentChange,
        hasExplicitConsent: customer?.c_tpaLearningCenterConsentData === true
    }
}

export default useTpaMarketingConsent
